/**
 * Encrypted on-device vault — Pro cross-device backup without a server.
 * Format: magic(4) + version(1) + salt(16) + iv(12) + ciphertext (AES-GCM)
 */
import { db } from './db';
import { isProEntitled } from './entitlement';

const MAGIC = new TextEncoder().encode('SPND');
const FORMAT_VERSION = 1;
const PBKDF2_ITERATIONS = 310000;
const SALT_LEN = 16;
const IV_LEN = 12;
const SCHEMA_VERSION = 2;

export const VAULT_PREF_KEYS = [
    'spenderly_monthly_income',
    'spenderly_monthly_fixed',
    'spenderly_target_savings',
    'spenderly_reality_done',
    'spenderly_leak_targets_set',
    'spenderly_pro_license',
    'spenderly_session',
    'spenderly_pro_account',
    'spenderly_recat_v3',
    'spenderly_recat_v4',
    'spenderly_recat_v5',
];

/** @deprecated prefer isProEntitled — kept as alias for existing call sites */
export function isProUnlocked(user) {
    return isProEntitled(user);
}

function concatBytes(...parts) {
    const total = parts.reduce((n, p) => n + p.length, 0);
    const out = new Uint8Array(total);
    let offset = 0;
    for (const p of parts) {
        out.set(p, offset);
        offset += p.length;
    }
    return out;
}

async function deriveKey(password, salt) {
    const enc = new TextEncoder();
    const material = await crypto.subtle.importKey(
        'raw',
        enc.encode(password),
        'PBKDF2',
        false,
        ['deriveKey']
    );
    return crypto.subtle.deriveKey(
        { name: 'PBKDF2', salt, iterations: PBKDF2_ITERATIONS, hash: 'SHA-256' },
        material,
        { name: 'AES-GCM', length: 256 },
        false,
        ['encrypt', 'decrypt']
    );
}

/** Snapshot Dexie + prefs into a plain JSON-serializable object */
export async function buildVaultPayload() {
    const [transactions, user_overrides, fixed_expenses, budgets, goals] = await Promise.all([
        db.transactions.toArray(),
        db.user_overrides.toArray(),
        db.fixed_expenses.toArray(),
        db.budgets.toArray(),
        db.goals.toArray(),
    ]);

    const prefs = {};
    for (const key of VAULT_PREF_KEYS) {
        const v = localStorage.getItem(key);
        if (v != null) prefs[key] = v;
    }

    let user = null;
    try {
        const raw = localStorage.getItem('user');
        if (raw) user = JSON.parse(raw);
    } catch { /* ignore */ }

    return {
        format: 'spenderly-vault',
        schemaVersion: SCHEMA_VERSION,
        exportedAt: new Date().toISOString(),
        appVersion: process.env.REACT_APP_VERSION || '1.0.0',
        user,
        prefs,
        tables: {
            transactions,
            user_overrides,
            fixed_expenses,
            budgets,
            goals,
        },
    };
}

/** Encrypt payload → Blob (.spenderly) */
export async function encryptVault(password, payload) {
    if (!password || password.length < 6) {
        throw new Error('Password must be at least 6 characters');
    }
    const salt = crypto.getRandomValues(new Uint8Array(SALT_LEN));
    const iv = crypto.getRandomValues(new Uint8Array(IV_LEN));
    const key = await deriveKey(password, salt);
    const plaintext = new TextEncoder().encode(JSON.stringify(payload));
    const cipherBuf = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, plaintext);
    const cipher = new Uint8Array(cipherBuf);
    const header = concatBytes(
        MAGIC,
        new Uint8Array([FORMAT_VERSION]),
        salt,
        iv,
        cipher
    );
    return new Blob([header], { type: 'application/octet-stream' });
}

/** Decrypt .spenderly ArrayBuffer → payload object */
export async function decryptVault(password, arrayBuffer) {
    const bytes = new Uint8Array(arrayBuffer);
    if (bytes.length < 4 + 1 + SALT_LEN + IV_LEN + 16) {
        throw new Error('File is too short to be a Spenderly vault');
    }
    const magic = String.fromCharCode(...bytes.slice(0, 4));
    if (magic !== 'SPND') {
        throw new Error('Not a Spenderly vault file');
    }
    const version = bytes[4];
    if (version !== FORMAT_VERSION) {
        throw new Error(`Unsupported vault version (${version})`);
    }
    const salt = bytes.slice(5, 5 + SALT_LEN);
    const iv = bytes.slice(5 + SALT_LEN, 5 + SALT_LEN + IV_LEN);
    const cipher = bytes.slice(5 + SALT_LEN + IV_LEN);
    const key = await deriveKey(password, salt);
    let plainBuf;
    try {
        plainBuf = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, cipher);
    } catch {
        throw new Error('Wrong password or corrupted file');
    }
    const text = new TextDecoder().decode(plainBuf);
    const payload = JSON.parse(text);
    if (!payload || payload.format !== 'spenderly-vault' || !payload.tables) {
        throw new Error('Invalid vault contents');
    }
    return payload;
}

/** Wipe tracked stores + prefs, then restore from payload */
export async function restoreVault(payload) {
    const tables = payload.tables || {};

    await db.transaction('rw',
        db.transactions,
        db.user_overrides,
        db.fixed_expenses,
        db.budgets,
        db.goals,
        async () => {
            await Promise.all([
                db.transactions.clear(),
                db.user_overrides.clear(),
                db.fixed_expenses.clear(),
                db.budgets.clear(),
                db.goals.clear(),
            ]);
            if (tables.transactions?.length) await db.transactions.bulkPut(tables.transactions);
            if (tables.user_overrides?.length) await db.user_overrides.bulkPut(tables.user_overrides);
            if (tables.fixed_expenses?.length) await db.fixed_expenses.bulkPut(tables.fixed_expenses);
            if (tables.budgets?.length) await db.budgets.bulkPut(tables.budgets);
            if (tables.goals?.length) await db.goals.bulkPut(tables.goals);
        }
    );

    for (const key of VAULT_PREF_KEYS) {
        localStorage.removeItem(key);
    }
    const prefs = payload.prefs || {};
    for (const [key, value] of Object.entries(prefs)) {
        if (value != null) localStorage.setItem(key, String(value));
    }

    if (payload.user && payload.user.id) {
        localStorage.setItem('user', JSON.stringify(payload.user));
    }
}

export function downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

export function vaultFilename() {
    const d = new Date().toISOString().slice(0, 10);
    return `spenderly-backup-${d}.spenderly`;
}

/** Plain CSV of transactions (Free export) */
export function transactionsToCsv(transactions) {
    let csv = 'Date,Merchant,Amount,Type,Category,Subcategory,Source,Confidence\n';
    for (const txn of transactions || []) {
        const amountRaw = txn.amount;
        const amount = typeof amountRaw === 'number'
            ? Math.abs(amountRaw)
            : Math.abs(parseFloat(String(amountRaw || '').replace(/[^0-9.-]/g, '')) || 0);
        const isDebit = txn.type && String(txn.type).toLowerCase() === 'debit';
        const party = String(txn.party || '').replace(/"/g, '""');
        csv += `"${txn.date_time || txn.date || ''}","${party}",${isDebit ? '-' : ''}${amount},"${txn.type || ''}","${txn.category || ''}","${txn.subcategory || ''}","${txn.source || ''}",${txn.confidence ?? ''}\n`;
    }
    return csv;
}

export function downloadCsv(transactions) {
    const csv = transactionsToCsv(transactions);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const d = new Date().toISOString().slice(0, 10);
    downloadBlob(blob, `spenderly-export-${d}.csv`);
}
